import { useMemo, useState } from "react";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { useCurrentModelMetrics, usePreviousModelMetrics, useMetricsLoading } from "@rapid/store";
import type { ModelMetrics } from "../types";
import { MetricStat, MetricCard, type MetricInsight } from "./MetricsComponents";

/**
 * Metrics display with vertical bars
 */
function MetricsDisplay({
    metrics,
    label,
    isCurrent = false
}: {
    metrics: ModelMetrics;
    label?: string;
    isCurrent?: boolean;
}) {
    const hasAnyMetrics =
        metrics.map !== undefined ||
        metrics.precision !== undefined ||
        metrics.recall !== undefined;

    if (!hasAnyMetrics) {
        return null;
    }

    return (
        <div className="flex flex-col">
            {label && (
                <span
                    className={twClassNames(
                        "w-fit rounded-t-lg border border-b-0 px-2.5 py-1.5 text-xs font-medium",
                        isCurrent
                            ? "border-green-500/20 bg-green-50 text-green-600"
                            : "border-gray-200 bg-gray-50 text-gray-500"
                    )}
                >
                    {label}
                </span>
            )}
            <div
                className={twClassNames(
                    "flex items-end justify-around gap-4 border p-4",
                    label ? "rounded-b-xl rounded-tr-xl" : "rounded-xl",
                    isCurrent ? "border-green-500/20 bg-green-50" : "border-gray-200 bg-gray-50"
                )}
            >
                <MetricStat
                    label={getMetricExplanation("map").shortTitle}
                    description={getMetricExplanation("map").description}
                    value={metrics.map}
                    color={isCurrent ? "green" : "gray"}
                />
                <MetricStat
                    label={getMetricExplanation("precision").shortTitle}
                    description={getMetricExplanation("precision").description}
                    value={metrics.precision}
                    color={isCurrent ? "green" : "gray"}
                />
                <MetricStat
                    label={getMetricExplanation("recall").shortTitle}
                    description={getMetricExplanation("recall").description}
                    value={metrics.recall}
                    color={isCurrent ? "green" : "gray"}
                />
            </div>
        </div>
    );
}

/**
 * Calculate insights with configurable thresholds
 * Only returns insights where there's a meaningful change to report
 */
function calculateInsights(
    current: { precision?: number; recall?: number; map?: number },
    previous: { precision?: number; recall?: number; map?: number },
    options: { minChangeThreshold?: number } = {}
): MetricInsight[] {
    const insights: MetricInsight[] = [];
    const minThreshold = options.minChangeThreshold ?? 1;

    if (
        current.precision !== undefined &&
        previous.precision !== undefined &&
        previous.precision > 0
    ) {
        const percentChange = ((current.precision - previous.precision) / previous.precision) * 100;

        if (Math.abs(percentChange) >= minThreshold) {
            const explanation = getMetricExplanation("precision");
            insights.push({
                ...explanation,
                change: percentChange,
                isImprovement: percentChange > 0
            });
        }
    }

    if (current.recall !== undefined && previous.recall !== undefined && previous.recall > 0) {
        const percentChange = ((current.recall - previous.recall) / previous.recall) * 100;

        if (Math.abs(percentChange) >= minThreshold) {
            const explanation = getMetricExplanation("recall");
            insights.push({
                ...explanation,
                change: percentChange,
                isImprovement: percentChange > 0
            });
        }
    }

    if (current.map !== undefined && previous.map !== undefined && previous.map > 0) {
        const percentChange = ((current.map - previous.map) / previous.map) * 100;

        if (Math.abs(percentChange) >= 0.5) {
            const explanation = getMetricExplanation("map");
            insights.push({
                ...explanation,
                change: percentChange,
                isImprovement: percentChange > 0
            });
        }
    }

    return insights;
}

/**
 * Section header for insights
 */
function InsightsHeader({
    title,
    className,
    collapsible = false,
    isOpen = false,
    onToggle
}: {
    title: string;
    className?: string;
    collapsible?: boolean;
    isOpen?: boolean;
    onToggle?: () => void;
}) {
    const baseClassName = "text-sm font-medium text-secondary font-sans";
    if (collapsible) {
        return (
            <button
                type="button"
                onClick={onToggle}
                className={twClassNames(
                    baseClassName,
                    "flex items-center gap-1.5 hover:text-primary focus:outline-none focus:ring-0",
                    className
                )}
            >
                {title}
                <i
                    className={twClassNames(
                        "far fa-chevron-down text-xs transition-transform",
                        isOpen && "rotate-180"
                    )}
                />
            </button>
        );
    }

    return (
        <span className={twClassNames(baseClassName, "cursor-default", className)}>{title}</span>
    );
}

function InsightsSection({
    title,
    children,
    className = "",
    collapsible = false
}: {
    title: string;
    children: React.ReactNode;
    className?: string;
    collapsible?: boolean;
}) {
    const [isOpen, setIsOpen] = useState(true);

    return (
        <div className={twClassNames("flex flex-col gap-3", className)}>
            <InsightsHeader
                title={title}
                collapsible={collapsible}
                isOpen={isOpen}
                onToggle={() => setIsOpen((prev) => !prev)}
            />
            {(!collapsible || isOpen) && children}
        </div>
    );
}

/**
 * Comparison insights section showing percentage changes
 */
function ComparisonInsightsSection({
    current,
    previous
}: {
    current: ModelMetrics;
    previous: ModelMetrics;
}) {
    const insights = useMemo(() => calculateInsights(current, previous), [current, previous]);

    return (
        <InsightsSection title="What Changed">
            {insights.length === 0 ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
                    <span className="text-sm text-tertiary">
                        No significant changes detected between versions
                    </span>
                </div>
            ) : (
                <div className="flex flex-col gap-1">
                    {insights.map((insight) => (
                        <MetricCard key={insight.id} insight={insight} />
                    ))}
                </div>
            )}
        </InsightsSection>
    );
}

/**
 * Get metric explanation for display
 * Uses layman-friendly terms
 */
function getMetricExplanation(metricType: "precision" | "recall" | "map"): {
    id: string;
    title: string;
    shortTitle: string;
    description: string;
} {
    switch (metricType) {
        case "precision":
            return {
                id: "label-accuracy",
                title: "Label Accuracy",
                shortTitle: "Accuracy",
                description:
                    "Measures how reliably your model identifies objects correctly without false alarms. Higher means fewer false alarms."
            };
        case "recall":
            return {
                id: "objects-covered",
                title: "Objects Covered",
                shortTitle: "Coverage",
                description:
                    "Measures how many real objects your model successfully detects. Higher means fewer missed objects."
            };
        case "map":
            return {
                id: "overall-performance",
                title: "Overall Performance",
                shortTitle: "Overall",
                description:
                    "Measures detection quality across all classes and scenarios. Higher means better detection quality."
            };
    }
}

/**
 * Loading skeleton for metrics
 */
function MetricsLoadingSkeleton() {
    return (
        <div className="flex flex-1 flex-col gap-2">
            <div className="h-16 animate-pulse rounded-lg bg-gray-100" />
            <div className="h-16 animate-pulse rounded-lg bg-gray-100" />
        </div>
    );
}

/**
 * Main component for displaying model comparison insights.
 * Shows human-readable insights (outcomes/KPIs) and raw metrics bars.
 */
export function ModelComparisonInsights() {
    const currentMetrics = useCurrentModelMetrics();
    const previousMetrics = usePreviousModelMetrics();
    const metricsLoading = useMetricsLoading();
    if (metricsLoading) {
        return <MetricsLoadingSkeleton />;
    }

    if (!currentMetrics) {
        return null;
    }

    // Only current metrics available (no comparison) - show absolute metrics with explanations
    if (!previousMetrics) {
        return (
            <div className="flex flex-col gap-4">
                <InsightsSection title="Raw Metrics">
                    <MetricsDisplay metrics={currentMetrics} />
                </InsightsSection>
            </div>
        );
    }

    return (
        <div className="content">
            <div className="flex flex-col gap-4 overflow-y-auto">
                <ComparisonInsightsSection current={currentMetrics} previous={previousMetrics} />

                <InsightsSection title="Raw Metrics" className="mb-5 mt-1" collapsible>
                    <div className="flex flex-col gap-4">
                        <MetricsDisplay
                            metrics={currentMetrics}
                            label="Current Version"
                            isCurrent={true}
                        />
                        <MetricsDisplay metrics={previousMetrics} label="Previous Version" />
                    </div>
                </InsightsSection>
            </div>
        </div>
    );
}
